import { useDerivedNetworkContext, useFilterContext } from '@/context';
import { useFlags } from 'launchdarkly-react-client-sdk';
import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

/**
 * Base type for additional parameters - all query hooks support at least showIds
 */
type BaseAdditionalParams = { showIds?: string[] };

/**
 * This hook is used to fetch data from the query hook.
 * @param queryHook - The query hook to fetch data from.
 * @param networkId - The network ID.
 * @param from - The start date.
 * @param to - The end date.
 * @param options - The options to pass to the query hook.
 * @param additionalParams - Additional parameters to merge with context-derived params.
 * @returns The query result.
 */
const useFetchQuery = <
  TData,
  TAdditionalParams extends BaseAdditionalParams = BaseAdditionalParams,
>(
  queryHook: (
    networkId: string,
    from: string,
    to: string,
    options?: Omit<UseQueryOptions<TData, Error>, 'queryKey' | 'queryFn'>,
    additionalParams?: TAdditionalParams
  ) => UseQueryResult<TData, Error>,
  options?: Omit<UseQueryOptions<TData, Error>, 'queryKey' | 'queryFn'>,
  additionalParams?: Omit<TAdditionalParams, 'showIds'>
) => {
  const { selectedNetwork } = useDerivedNetworkContext();
  const { filters } = useFilterContext();
  const { selectedShows } = filters;
  const { from, to } = filters.dateRange;
  const { audienceSources } = useFlags<{ audienceSources?: string[] }>();
  const sources = audienceSources ?? ['acast', 'phyllo'];

  const showIds = selectedShows.length > 0 ? selectedShows : undefined;

  const mergedAdditionalParams = {
    showIds,
    ...additionalParams,
    ...(additionalParams && 'sources' in additionalParams ? { sources } : {}),
  } as TAdditionalParams;

  const queryResult = queryHook(
    selectedNetwork.id ?? '',
    from,
    to,
    options,
    mergedAdditionalParams
  );

  return { queryResult };
};

export default useFetchQuery;
